import type { PendingBatchData, PendingSearchData, SessionStorageData } from '@/types';
import { logger } from '@/utils/logger';

const SESSION_STORAGE_KEY = 'collectability_pending_data';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

export class SessionStorageService {
  /**
   * Save pending batch data to sessionStorage
   * Data will expire after 30 minutes
   */
  savePendingBatch(batchData: Omit<PendingBatchData, 'timestamp' | 'sessionId'>): string {
    const sessionId = this.generateSessionId();
    const pendingBatch: PendingBatchData = {
      ...batchData,
      timestamp: Date.now(),
      sessionId,
    };

    try {
      const currentData = this.getSessionData();
      currentData.pendingBatches.push(pendingBatch);
      this.setSessionData(currentData);

      logger.info('Saved pending batch to sessionStorage', { sessionId });
      return sessionId;
    } catch (error) {
      logger.error('Failed to save pending batch:', error);
      throw new Error('Failed to save batch data. Please try again.');
    }
  }

  /**
   * Save pending search data to sessionStorage
   * Data will expire after 30 minutes
   */
  savePendingSearch(searchData: Omit<PendingSearchData, 'timestamp' | 'sessionId'>): string {
    const sessionId = this.generateSessionId();
    const pendingSearch: PendingSearchData = {
      ...searchData,
      timestamp: Date.now(),
      sessionId,
    };

    try {
      const currentData = this.getSessionData();
      currentData.pendingSearches.push(pendingSearch);
      this.setSessionData(currentData);

      logger.info('Saved pending search to sessionStorage', { sessionId });
      return sessionId;
    } catch (error) {
      logger.error('Failed to save pending search:', error);
      throw new Error('Failed to save search data. Please try again.');
    }
  }

  /**
   * Get all valid (non-expired) pending batches
   */
  getPendingBatches(): PendingBatchData[] {
    this.cleanupExpiredData();
    const data = this.getSessionData();
    return data.pendingBatches;
  }

  /**
   * Get all valid (non-expired) pending searches
   */
  getPendingSearches(): PendingSearchData[] {
    this.cleanupExpiredData();
    const data = this.getSessionData();
    return data.pendingSearches;
  }

  /**
   * Get a specific pending batch by sessionId
   */
  getPendingBatch(sessionId: string): PendingBatchData | null {
    const batches = this.getPendingBatches();
    return batches.find(b => b.sessionId === sessionId) || null;
  }

  /**
   * Get a specific pending search by sessionId
   */
  getPendingSearch(sessionId: string): PendingSearchData | null {
    const searches = this.getPendingSearches();
    return searches.find(s => s.sessionId === sessionId) || null;
  }

  /**
   * Check if there is any pending data (for showing prompts)
   */
  hasPendingData(): boolean {
    this.cleanupExpiredData();
    const data = this.getSessionData();
    return data.pendingBatches.length > 0 || data.pendingSearches.length > 0;
  }

  /**
   * Clear all pending data (called after successful login and data persistence)
   */
  clearPendingData(): void {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      logger.info('Cleared all pending data from sessionStorage');
    } catch (error) {
      logger.error('Failed to clear pending data:', error);
    }
  }

  /**
   * Clear a specific pending batch by sessionId
   */
  clearPendingBatch(sessionId: string): void {
    try {
      const data = this.getSessionData();
      data.pendingBatches = data.pendingBatches.filter(b => b.sessionId !== sessionId);
      this.setSessionData(data);
      logger.info('Cleared pending batch', { sessionId });
    } catch (error) {
      logger.error('Failed to clear pending batch:', error);
    }
  }

  /**
   * Clear a specific pending search by sessionId
   */
  clearPendingSearch(sessionId: string): void {
    try {
      const data = this.getSessionData();
      data.pendingSearches = data.pendingSearches.filter(s => s.sessionId !== sessionId);
      this.setSessionData(data);
      logger.info('Cleared pending search', { sessionId });
    } catch (error) {
      logger.error('Failed to clear pending search:', error);
    }
  }

  /**
   * Remove expired data from sessionStorage
   */
  private cleanupExpiredData(): void {
    try {
      const data = this.getSessionData();
      const now = Date.now();

      const validBatches = data.pendingBatches.filter(
        batch => now - batch.timestamp < SESSION_TTL_MS
      );
      const validSearches = data.pendingSearches.filter(
        search => now - search.timestamp < SESSION_TTL_MS
      );

      const expiredCount =
        (data.pendingBatches.length - validBatches.length) +
        (data.pendingSearches.length - validSearches.length);

      if (expiredCount > 0) {
        logger.info(`Cleaned up ${expiredCount} expired session items`);
        this.setSessionData({
          pendingBatches: validBatches,
          pendingSearches: validSearches,
        });
      }
    } catch (error) {
      logger.error('Failed to cleanup expired data:', error);
    }
  }

  /**
   * Get session data from sessionStorage
   */
  private getSessionData(): SessionStorageData {
    try {
      const dataJson = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!dataJson) {
        return { pendingBatches: [], pendingSearches: [] };
      }

      const data: SessionStorageData = JSON.parse(dataJson);
      return {
        pendingBatches: data.pendingBatches || [],
        pendingSearches: data.pendingSearches || [],
      };
    } catch (error) {
      logger.error('Failed to parse session data:', error);
      return { pendingBatches: [], pendingSearches: [] };
    }
  }

  /**
   * Save session data to sessionStorage
   */
  private setSessionData(data: SessionStorageData): void {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Failed to save session data:', error);
      throw new Error('Failed to save session data');
    }
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Get time remaining for a session (in milliseconds)
   */
  getTimeRemaining(timestamp: number): number {
    const elapsed = Date.now() - timestamp;
    const remaining = SESSION_TTL_MS - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Format time remaining as human-readable string
   */
  formatTimeRemaining(timestamp: number): string {
    const remaining = this.getTimeRemaining(timestamp);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
}

export const sessionStorageService = new SessionStorageService();
